import styled, { keyframes } from 'styled-components';
import {
  surface,
  brandDark,
  primary,
  primarySoft,
  muted,
  text,
  borderSoft,
  shadowSoft,
  radiusLg,
  radiusPill,
} from '../../../Layout/Resources/Theme';

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const Box = styled.div`
  position: fixed;
  inset: 0;
  display: grid;
  grid-template-columns: minmax(280px, 1.05fr) minmax(320px, 0.95fr);
  width: 100%;
  height: 100%;
  background: #F2F6F3;
  overflow: auto;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

Box.Brand = styled.aside`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 3rem 3.25rem;
  color: #fff;
  background:
    radial-gradient(ellipse 80% 60% at 12% 8%, rgba(168, 200, 64, 0.22) 0%, transparent 55%),
    radial-gradient(ellipse 70% 50% at 88% 92%, rgba(40, 104, 64, 0.55) 0%, transparent 50%),
    linear-gradient(160deg, #0A1F16 0%, ${brandDark} 45%, #1B4D30 100%);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 420px;
    height: 420px;
    border-radius: 50%;
    border: 1px solid rgba(168, 200, 64, 0.18);
    top: -120px;
    right: -140px;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    width: 280px;
    height: 280px;
    border-radius: 50%;
    background: rgba(168, 200, 64, 0.12);
    bottom: -80px;
    left: -60px;
    pointer-events: none;
  }

  @media (max-width: 900px) {
    min-height: 220px;
    padding: 2rem 1.5rem 1.75rem;
    justify-content: center;
    align-items: center;
    text-align: center;
  }
`

Box.BrandTop = styled.div`
  position: relative;
  z-index: 1;
  animation: ${fadeUp} 0.55s ease both;
`

Box.BrandCopy = styled.div`
  position: relative;
  z-index: 1;
  max-width: 420px;
  animation: ${fadeUp} 0.65s ease 0.08s both;

  h1 {
    margin: 0 0 0.85rem;
    font-size: clamp(1.75rem, 3vw, 2.35rem);
    font-weight: 700;
    letter-spacing: -0.03em;
    line-height: 1.2;
    color: #fff;
  }

  p {
    margin: 0;
    font-size: 1rem;
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.72);
    font-weight: 400;
  }

  @media (max-width: 900px) {
    display: none;
  }
`

Box.BrandFooter = styled.div`
  position: relative;
  z-index: 1;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.45);
  letter-spacing: 0.02em;

  @media (max-width: 900px) {
    display: none;
  }
`

Box.Logo = styled.img`
  max-width: 210px;
  width: 100%;
  height: auto;
  display: block;
  filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.25));

  @media (max-width: 900px) {
    max-width: 170px;
    margin: 0 auto;
  }
`

Box.Panel = styled.main`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 2rem;
  background:
    linear-gradient(180deg, #F7FAF7 0%, #EDF4EF 100%);

  @media (max-width: 900px) {
    padding: 1.5rem 1.15rem 2.5rem;
    align-items: flex-start;
  }
`

Box.Container = styled.div`
  width: 100%;
  max-width: 420px;
  animation: ${fadeUp} 0.6s ease 0.12s both;
`

Box.Content = styled.div`
  border-radius: ${radiusLg};
  padding: 2.25rem 2rem;
  background: ${surface};
  border: 1px solid ${borderSoft};
  box-shadow: ${shadowSoft}, 0 20px 50px rgba(49, 57, 72, 0.08);

  .form-control,
  .input-group-text {
    min-height: 46px;
  }

  .input-group-text {
    background: #f5f8fc;
    color: ${muted};
  }

  .form-control {
    border-radius: 12px;
    background: #F7FAF7;
  }

  .form-control:focus {
    background: #fff;
  }

  .btn-login {
    height: 50px;
    border-radius: ${radiusPill};
    font-weight: 700;
    letter-spacing: 0.06em;
    background: ${primary};
    border-color: ${primary};
    box-shadow: 0 8px 20px rgba(40, 104, 64, 0.28);
  }

  .btn-login:hover,
  .btn-login:focus,
  .btn-login:active {
    background: #1B4D30 !important;
    border-color: #1B4D30 !important;
  }

  .custom-control-label {
    color: ${text};
    font-size: 0.875rem;
  }

  @media (max-width: 900px) {
    padding: 1.75rem 1.35rem;
  }
`

Box.Title = styled.h2`
  margin: 0 0 0.35rem;
  font-size: 1.45rem;
  font-weight: 700;
  color: ${brandDark};
  letter-spacing: -0.02em;
`

Box.Hint = styled.p`
  margin: 0 0 1.75rem;
  font-size: 0.9rem;
  color: ${muted};
  line-height: 1.45;
`

Box.Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 1.15rem;
  padding: 0.35rem 0.85rem;
  border-radius: ${radiusPill};
  background: ${primarySoft};
  color: ${primary};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
`

export default Box
